import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { REGIONS, CATEGORIES } from '../data/mockData';
import { PropertyCategory, PropertySubcategory, AdType, AdKind, HeatingType, PropertyFeatures, Property } from '../types';
import { client } from '../api/client';
import { useI18n } from '../i18n';
import { LocationSearch } from '../components/common/LocationSearch';
import { LocationPickerMap } from '../components/common/LocationPickerMap';
import { SubregionSearch } from '../components/common/SubregionSearch';
import { useToast } from '../components/common/Toast';

// Tip za trajanje oglasa
interface ListingDuration {
  id: number;
  days: number;
  tokens: number;
  label: string;
  is_active: boolean;
}

// Key za sessionStorage (briše se kad se zatvori tab)
const FORM_STORAGE_KEY = 'new_listing_form_data';
const DRAFT_STORAGE_KEY = 'new_listing_draft';

export function NewListingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isChecking, user, addProperty, tokenBalance, setTokenBalance, deductTokens } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [images, setImages] = useState<string[]>([]);
  const [locationCoords, setLocationCoords] = useState<{ lat?: number; lng?: number }>({});
  const [durationOptions, setDurationOptions] = useState<ListingDuration[]>([]);
  const [loadingDurations, setLoadingDurations] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);

  // Definiši defaultFormData pre upotrebe
  const getDefaultFormData = useCallback(() => ({
    adKind: 'Ponuda' as AdKind,
    adType: 'Prodaja' as AdType,
    category: '' as PropertyCategory | '',
    subcategory: '' as PropertySubcategory | '',
    title: '',
    description: '',
    price: '',
    priceMin: '',
    priceMax: '',
    currency: 'EUR' as 'EUR' | 'RSD',
    squareMeters: '',
    squareMetersMin: '',
    squareMetersMax: '',
    rooms: '',
    roomsMin: '',
    roomsMax: '',
    floor: '',
    floorMin: '',
    floorMax: '',
    totalFloors: '',
    heating: '' as HeatingType | '',
    heatingTypes: [] as HeatingType[],
    utilities: '',
    plotArea: '',
    plotAreaMin: '',
    plotAreaMax: '',
    region: '',
    city: '',
    address: '',
    nearbyLocation: '',
    contactPhone: (user as any)?.phone || user?.phone || '',
    contactEmail: user?.email || '',
    duration: 30,
    features: {} as Partial<PropertyFeatures>,
    isUrgent: false,
  }), [user]);

  // Učitaj sačuvane podatke iz sessionStorage (NE localStorage - briše se kad se zatvori tab)
  const getSavedFormData = useCallback(() => {
    const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const [formData, setFormData] = useState(() => getSavedFormData() || getDefaultFormData());

  // Očisti sessionStorage kada se komponenta unmountuje (korisnik napušta stranicu)
  useEffect(() => {
    return () => {
      // Briši formu kada korisnik napusti stranicu
      sessionStorage.removeItem(FORM_STORAGE_KEY);
    };
  }, []);

  // Očisti sessionStorage kada se komponenta unmountuje nakon uspešne objave
  const clearFormData = useCallback(() => {
    sessionStorage.removeItem(FORM_STORAGE_KEY);
    setFormData(getDefaultFormData());
    setImages([]);
    setStep(1);
  }, [getDefaultFormData]);

  // Učitaj tokene iz baze
  useEffect(() => {
    if (isAuthenticated) {
      loadUserTokens();
    }
  }, [isAuthenticated]);

  // Učitaj cene trajanja iz baze - ponovo učitaj kad se promeni tip oglasa
  useEffect(() => {
    loadDurationPrices(formData.adKind);
  }, [formData.adKind]);

  // Učitaj draft ako postoji (na mount)
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (draft && !getSavedFormData()) {
      try {
        const draftData = JSON.parse(draft);
        // Pitaj korisnika da li želi da nastavi sa draftom
        const wantsToContinue = window.confirm(
          language === 'sr' 
            ? `Imate sačuvan draft oglasa "${draftData.title}" od ${new Date(draftData.savedAt).toLocaleDateString()}. Želite li da nastavite sa njim?`
            : `You have a saved draft "${draftData.title}" from ${new Date(draftData.savedAt).toLocaleDateString()}. Do you want to continue with it?`
        );
        
        if (wantsToContinue) {
          const { images: draftImages, savedAt, ...restData } = draftData;
          setFormData(restData);
          if (draftImages) setImages(draftImages);
          localStorage.removeItem(DRAFT_STORAGE_KEY); // Obriši draft nakon učitavanja
        } else {
          localStorage.removeItem(DRAFT_STORAGE_KEY); // Korisnik je odbio, obriši draft
        }
      } catch {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
  }, [getSavedFormData, language]);

  const loadDurationPrices = async (adKind: AdKind) => {
    setLoadingDurations(true);
    try {
      const res = await client.api.fetch(`/api/pricing/listing-durations?ad_kind=${encodeURIComponent(adKind)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.data && Array.isArray(data.data)) {
          // Transform data - ensure proper types and generate label if missing
          const transformedData = data.data.map((d: any) => ({
            id: d.id,
            days: Number(d.days),
            tokens: Number(d.tokens),
            label: d.label || `${d.days} dana`,
            is_active: Boolean(d.is_active) || d.is_active === 1 || d.is_active === '1'
          }));
          setDurationOptions(transformedData);
          
          // Ako trenutni duration nije u opcijama, postavi prvi
          if (transformedData.length > 0 && !transformedData.find((d: ListingDuration) => d.days === formData.duration)) {
            setFormData((prev: typeof formData) => ({ ...prev, duration: transformedData[0].days }));
          }
        }
      } else {
        throw new Error('API error');
      }
    } catch (err) {
      console.error('Failed to load duration prices:', err);
      // Fallback to defaults
      setDurationOptions([
        { id: 1, days: 7, tokens: 2, label: '7 dana', is_active: true },
        { id: 2, days: 15, tokens: 3, label: '15 dana', is_active: true },
        { id: 3, days: 30, tokens: 5, label: '30 dana', is_active: true },
      ]);
    } finally {
      setLoadingDurations(false);
    }
  };

  const loadUserTokens = async () => {
    try {
      const res = await client.api.fetch('/api/tokens');
      if (res.ok) {
        const data = await res.json();
        if (data.data?.balance !== undefined) {
          setTokenBalance(data.data.balance);
        }
      } else {
        // Fallback to local user tokens if API fails (e.g. 401)
        if (user?.tokenBalance !== undefined) {
          // Don't overwrite if we already have a balance in store which might be more up to date
          // But here we are loading initial state
          // setTokenBalance(user.tokenBalance);
        }
      }
    } catch (err) {
      console.error('Failed to load tokens:', err);
    } finally {
      setLoadingTokens(false);
    }
  };

  // Back button component
  const BackButton = () => (
    <button
      onClick={() => navigate(-1)}
      className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-4 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span>{language === 'sr' ? 'Nazad' : 'Back'}</span>
    </button>
  );

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8"
          >
            <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Potrebna prijava' : 'Login Required'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {language === 'sr' ? 'Za objavljivanje oglasa morate biti prijavljeni.' : 'You must be logged in to post a listing.'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/prijava')}
                className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Prijavi se' : 'Login'}
              </button>
              <button
                onClick={() => navigate('/registracija')}
                className="px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                {language === 'sr' ? 'Registruj se' : 'Register'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Provera email verifikacije
  if (!user?.emailVerified) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8"
          >
            <svg className="w-16 h-16 mx-auto text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {language === 'sr' ? 'Potrebna verifikacija email-a' : 'Email Verification Required'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {language === 'sr' 
                ? 'Za objavljivanje oglasa morate prvo verifikovati vašu email adresu. Proverite vaš inbox za verifikacioni link.' 
                : 'To post a listing, you must first verify your email address. Check your inbox for the verification link.'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/profil')}
                className="px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Idi na profil' : 'Go to Profile'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                {language === 'sr' ? 'Početna' : 'Home'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const selectedRegion = REGIONS.find(r => r.name === formData.region);
  const selectedCategory = CATEGORIES.find(c => c.name === formData.category);
  const selectedDuration = durationOptions.find(d => d.days === formData.duration);
  const tokensRequired = selectedDuration?.tokens || 3;

  // Max file sizes
  const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB per image
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB per video
  const MAX_MEDIA_COUNT = 15; // max 15 media items total

  // Compress image before upload
  const compressImage = (file: File, maxWidth: number = 1920, quality: number = 0.85): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if larger than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Read file as data URL
  const readFileAsDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle media upload (images + videos, up to 15 items)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_MEDIA_COUNT - images.length;
    if (remainingSlots <= 0) {
      toast.warning(language === 'sr' 
        ? `Maksimalan broj fajlova je ${MAX_MEDIA_COUNT}` 
        : `Maximum number of files is ${MAX_MEDIA_COUNT}`);
      return;
    }

    const allFiles = Array.from(files);
    if (allFiles.length > remainingSlots) {
      toast.warning(language === 'sr' 
        ? `Možete dodati još ${remainingSlots} fajl(ova). Odabrano: ${allFiles.length}` 
        : `You can add ${remainingSlots} more file(s). Selected: ${allFiles.length}`);
    }

    const filesToAdd = allFiles.slice(0, remainingSlots);
    let skippedSize = 0;
    let skippedType = 0;

    for (const file of filesToAdd) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isImage && !isVideo) {
        skippedType++;
        continue;
      }

      // Check file size
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        skippedSize++;
        continue;
      }

      try {
        if (isVideo) {
          // Videos: store as data URL directly
          const dataUrl = await readFileAsDataUrl(file);
          setImages(prev => [...prev, dataUrl].slice(0, MAX_MEDIA_COUNT));
        } else {
          // Images: compress before storing
          const compressedImage = await compressImage(file);
          setImages(prev => [...prev, compressedImage].slice(0, MAX_MEDIA_COUNT));
        }
      } catch (error) {
        console.error('Error processing file:', error);
        // Fallback to original if compression fails
        try {
          const dataUrl = await readFileAsDataUrl(file);
          setImages(prev => [...prev, dataUrl].slice(0, MAX_MEDIA_COUNT));
        } catch {
          // Skip this file
        }
      }
    }

    if (skippedSize > 0) {
      toast.error(language === 'sr' 
        ? `${skippedSize} fajl(ova) preskočeno - preveliki (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB za slike, ${MAX_VIDEO_SIZE / 1024 / 1024}MB za video)` 
        : `${skippedSize} file(s) skipped - too large (max ${MAX_IMAGE_SIZE / 1024 / 1024}MB for images, ${MAX_VIDEO_SIZE / 1024 / 1024}MB for video)`);
    }
    if (skippedType > 0) {
      toast.error(language === 'sr' 
        ? `${skippedType} fajl(ova) preskočeno - nepodržan format (samo slike i video)` 
        : `${skippedType} file(s) skipped - unsupported format (images and video only)`);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleBuyTokens = async () => {
    // Auto-save as draft before navigating to buy tokens
    if (user && formData.title.trim()) {
      try {
        setSavingDraft(true);
        
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + formData.duration);

        const draftData = new FormData();
        draftData.append('ad_kind', formData.adKind);
        draftData.append('ad_type', formData.adType);
        draftData.append('category', formData.category);
        if (formData.subcategory) draftData.append('subcategory', formData.subcategory);
        draftData.append('title', formData.title || 'Draft');
        draftData.append('description', formData.description);
        draftData.append('price', formData.price);
        draftData.append('currency', formData.currency);
        draftData.append('region', formData.region);
        draftData.append('city', formData.city);
        draftData.append('address', formData.address);
        draftData.append('area', formData.area);
        draftData.append('rooms', formData.rooms);
        draftData.append('floor', formData.floor);
        draftData.append('total_floors', formData.totalFloors);
        draftData.append('year_built', formData.yearBuilt);
        draftData.append('features', JSON.stringify(formData.features));
        draftData.append('contact_phone', formData.contactPhone);
        draftData.append('contact_email', formData.contactEmail);
        draftData.append('duration', String(formData.duration));
        draftData.append('expires_at', expiresAt.toISOString());
        draftData.append('status', 'draft');
        
        // Add images
        for (let i = 0; i < images.length; i++) {
          const response = await fetch(images[i]);
          const blob = await response.blob();
          draftData.append('images[]', blob, `image${i}.jpg`);
        }

        await client.api.fetch('/api/properties', {
          method: 'POST',
          body: draftData,
        });
        
        // Clear localStorage after successful save
        localStorage.removeItem('newListingDraft');
        
        toast.success(
          language === 'sr' ? 'Sačuvano' : 'Saved',
          language === 'sr' ? 'Oglas je sačuvan kao priprema.' : 'Listing saved as draft.'
        );
      } catch (error) {
        console.error('Failed to save draft:', error);
        // Continue to navigate even if save fails - localStorage still has the data
      } finally {
        setSavingDraft(false);
      }
    }
    
    navigate('/kupi-tokene');
  };

  const handleSubmit = async (asDraft: boolean = false) => {
    if (!user) return;
    
    // Validacija obaveznog telefona
    if (!formData.contactPhone.trim()) {
      toast.warning(
        language === 'sr' ? 'Nedostaje telefon' : 'Phone missing',
        language === 'sr' ? 'Broj telefona je obavezan!' : 'Phone number is required!'
      );
      return;
    }
    
    if (!asDraft && tokenBalance < tokensRequired) {
      return;
    }

    setIsSubmitting(true);

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + formData.duration);

    const newProperty: Property = {
      id: `p${Date.now()}`,
      adKind: formData.adKind,
      adType: formData.adType,
      category: formData.category as PropertyCategory,
      subcategory: formData.subcategory as PropertySubcategory || undefined,
      title: formData.title,
      description: formData.description,
      price: Number(formData.price),
      currency: formData.currency,
      squareMeters: Number(formData.squareMeters) || 0,
      rooms: formData.rooms ? Number(formData.rooms) : undefined,
      floor: formData.floor ? Number(formData.floor) : undefined,
      totalFloors: formData.totalFloors ? Number(formData.totalFloors) : undefined,
      heating: formData.heating as HeatingType || undefined,
      utilities: formData.utilities ? Number(formData.utilities) : undefined,
      plotArea: formData.plotArea ? Number(formData.plotArea) : undefined,
      location: {
        region: formData.region,
        city: formData.city,
        address: formData.address,
        // Koristi koordinate iz OSM pretrage ako postoje, inače iz mock podataka
        lat: locationCoords.lat || selectedRegion?.cities.find(c => c.name === formData.city)?.lat,
        lng: locationCoords.lng || selectedRegion?.cities.find(c => c.name === formData.city)?.lng,
      },
      features: formData.features as PropertyFeatures,
      images: images,
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
      userId: user.id,
      views: 0,
      approved: asDraft ? false : (user.type === 'admin' || (user as any).role === 'admin'),
      featured: false,
      createdAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString(),
      status: asDraft ? 'draft' : ((user.type === 'admin' || (user as any).role === 'admin') ? 'active' : 'pending'),
    };

    try {
      // Pokušaj da kreiraj oglas preko API-ja
      let propertyCreated = false;
      try {
        // Pripremi podatke zavisno od vrste oglasa
        const isPotraznja = formData.adKind === 'Potražnja';
        
        const requestData: Record<string, any> = {
          title: formData.title,
          description: formData.description,
          adKind: formData.adKind,
          adType: formData.adType,
          category: formData.category,
          subcategory: formData.subcategory,
          currency: formData.currency || 'EUR',
          city: formData.city,
          region: formData.region,
          images: images,
          contactPhone: formData.contactPhone,
          contactEmail: formData.contactEmail,
          duration: formData.duration,
          features: formData.features,
          saveAsDraft: asDraft,
          isUrgent: formData.isUrgent || false,
        };

        if (isPotraznja) {
          // Za Potražnju šaljemo opsege
          requestData.priceMin = formData.priceMin || null;
          requestData.priceMax = formData.priceMax || null;
          requestData.squareMetersMin = formData.squareMetersMin || null;
          requestData.squareMetersMax = formData.squareMetersMax || null;
          requestData.roomsMin = formData.roomsMin || null;
          requestData.roomsMax = formData.roomsMax || null;
          requestData.floorMin = formData.floorMin || null;
          requestData.floorMax = formData.floorMax || null;
          requestData.plotAreaMin = formData.plotAreaMin || null;
          requestData.plotAreaMax = formData.plotAreaMax || null;
          requestData.heatingTypes = formData.heatingTypes || [];
          requestData.nearbyLocation = formData.nearbyLocation || null;
          // Za Potražnju ne šaljemo tačnu adresu i koordinate
        } else {
          // Za Ponudu i Aukciju šaljemo tačne vrednosti
          requestData.price = formData.price;
          requestData.squareMeters = formData.squareMeters;
          requestData.rooms = formData.rooms;
          requestData.floor = formData.floor;
          requestData.totalFloors = formData.totalFloors;
          requestData.heatingType = formData.heating;
          requestData.plotArea = formData.plotArea;
          requestData.address = formData.address;
          requestData.lat = locationCoords.lat;
          requestData.lng = locationCoords.lng;
        }

        const propertyRes = await client.api.fetch('/properties', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        });

        if (propertyRes.ok) {
          propertyCreated = true;
          // Osveži balans sa servera
          await loadUserTokens();
          // Obriši sačuvane podatke forme
          sessionStorage.removeItem(FORM_STORAGE_KEY);
          setTimeout(() => {
            setIsSubmitting(false);
            navigate('/moji-oglasi');
          }, 1000);
        } else {
          const err = await propertyRes.json();

          // Proveri da li korisnik mora da verifikuje email
          if (err.needsVerification || propertyRes.status === 403) {
            toast.warning(
              language === 'sr' ? 'Verifikacija potrebna' : 'Verification needed',
              language === 'sr'
                ? 'Morate verifikovati vašu email adresu pre nego što možete objaviti oglas. Proverite vaš inbox za verifikacioni link.'
                : 'You must verify your email address before posting a listing. Check your inbox for verification link.'
            );
            setIsSubmitting(false);
            return;
          }

          // Proveri da li nema dovoljno tokena
          if (propertyRes.status === 402) {
            toast.warning(
              language === 'sr' ? 'Nedovoljno tokena' : 'Insufficient tokens',
              language === 'sr'
                ? 'Nemate dovoljno tokena za objavu oglasa. Kupite dodatne tokene.'
                : 'Insufficient tokens to post listing. Purchase more tokens.'
            );
            setIsSubmitting(false);
            navigate('/kupovina-tokena');
            return;
          }

          // Za 401 ili drugu grešku, probaj lokalno ako je mock korisnik
          if (propertyRes.status === 401 || propertyRes.status === 404) {
            console.warn('API property creation failed, falling back to local state');
            // Fallback to local state
            if (deductTokens(tokensRequired)) {
              addProperty(newProperty);
              sessionStorage.removeItem(FORM_STORAGE_KEY);
              setTimeout(() => {
                setIsSubmitting(false);
                navigate('/moji-oglasi');
              }, 1000);
              return;
            }
          }

          toast.error(
            language === 'sr' ? 'Greška' : 'Error',
            err.error || (language === 'sr' ? 'Greška pri objavi oglasa' : 'Error posting listing')
          );
          setIsSubmitting(false);
        }
      } catch (apiError) {
        console.warn('API error, falling back to local state:', apiError);
        // Fallback to local state on network error
        if (deductTokens(tokensRequired)) {
          addProperty(newProperty);
          sessionStorage.removeItem(FORM_STORAGE_KEY);
          setTimeout(() => {
            setIsSubmitting(false);
            navigate('/moji-oglasi');
          }, 1000);
        } else {
          toast.error(
            language === 'sr' ? 'Nedovoljno tokena' : 'Insufficient tokens',
            language === 'sr' ? 'Nemate dovoljno tokena' : 'You don\'t have enough tokens'
          );
          setIsSubmitting(false);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri objavi oglasa' : 'Error posting listing'
      );
      setIsSubmitting(false);
    }
  };

  const featureOptions: { key: keyof PropertyFeatures; label: string }[] = [
    { key: 'odmahUseljiv', label: language === 'sr' ? 'Odmah useljiv' : 'Move-in ready' },
    { key: 'uknjizen', label: language === 'sr' ? 'Uknjižen' : 'Registered' },
    { key: 'lift', label: language === 'sr' ? 'Lift' : 'Elevator' },
    { key: 'terasa', label: language === 'sr' ? 'Terasa' : 'Terrace' },
    { key: 'garaza', label: language === 'sr' ? 'Garaža' : 'Garage' },
    { key: 'parkingMesto', label: language === 'sr' ? 'Parking mesto' : 'Parking' },
    { key: 'klima', label: language === 'sr' ? 'Klima' : 'AC' },
    { key: 'internet', label: 'Internet' },
    { key: 'videoNadzor', label: language === 'sr' ? 'Video nadzor' : 'Security' },
    { key: 'struja', label: language === 'sr' ? 'Struja' : 'Electricity' },
    { key: 'voda', label: language === 'sr' ? 'Voda' : 'Water' },
    { key: 'kanalizacija', label: language === 'sr' ? 'Kanalizacija' : 'Sewage' },
    { key: 'dozvoljenaGradnja', label: language === 'sr' ? 'Dozvoljena gradnja' : 'Building permit' },
  ];

  // Konfiguracija polja forme po kategoriji (na osnovu Excel tabele)
  interface CategoryFormConfig {
    showRooms: boolean;
    showHeating: boolean;
    showSquareMeters: boolean;
    showFloor: boolean;
    showPlotArea: boolean;
    showUtilities: boolean;
    features: (keyof PropertyFeatures)[];
  }

  const CATEGORY_FORM_CONFIG: Record<string, CategoryFormConfig> = {
    'Kuća': {
      showRooms: true,
      showHeating: true,
      showSquareMeters: true,
      showFloor: false,
      showPlotArea: true,
      showUtilities: true,
      features: ['odmahUseljiv', 'uknjizen', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda', 'kanalizacija', 'lift']
    },
    'Stan': {
      showRooms: true,
      showHeating: true,
      showSquareMeters: true,
      showFloor: true,
      showPlotArea: false,
      showUtilities: true,
      features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda']
    },
    'Poslovni prostor': {
      showRooms: true,
      showHeating: false,
      showSquareMeters: true,
      showFloor: true,
      showPlotArea: false,
      showUtilities: true,
      features: ['lift', 'klima', 'internet', 'garaza', 'parkingMesto', 'videoNadzor', 'struja', 'voda', 'kanalizacija']
    },
    'Plac': {
      showRooms: false,
      showHeating: false,
      showSquareMeters: false,
      showFloor: false,
      showPlotArea: true,
      showUtilities: false,
      features: ['uknjizen', 'struja', 'voda', 'kanalizacija', 'dozvoljenaGradnja']
    },
    'Garaža': {
      showRooms: false,
      showHeating: false,
      showSquareMeters: false,
      showFloor: false,
      showPlotArea: false,
      showUtilities: false,
      features: ['uknjizen', 'videoNadzor']
    }
  };

  // Funkcija za dobijanje konfiguracije forme za kategoriju
  const getFormConfig = (category: string | ''): CategoryFormConfig => {
    if (!category) {
      // Default - prikaži sva polja
      return {
        showRooms: true,
        showHeating: true,
        showSquareMeters: true,
        showFloor: true,
        showPlotArea: true,
        showUtilities: true,
        features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'struja', 'voda', 'kanalizacija', 'dozvoljenaGradnja']
      };
    }
    return CATEGORY_FORM_CONFIG[category] || CATEGORY_FORM_CONFIG['Stan'];
  };

  const formConfig = getFormConfig(formData.category);
  
  // Filtrirane karakteristike na osnovu kategorije
  const filteredFeatureOptions = featureOptions.filter(f => 
    formConfig.features.includes(f.key)
  );

  // Validacija koraka 2 - dinamički na osnovu vrste oglasa i kategorije
  const validateStep2 = (): boolean => {
    const isPotraznja = formData.adKind === 'Potražnja';
    
    // Validacija cene
    if (isPotraznja) {
      // Za Potražnju - bar jedna granica opsega mora biti unesena (ili oba opciono)
      // Cena nije striktno obavezna za potražnju
    } else {
      // Za Ponudu/Aukciju - cena je obavezna
      if (!formData.price || formData.price.trim() === '') {
        return false;
      }
    }
    
    // Validacija lokacije
    if (isPotraznja) {
      // Za Potražnju - region je obavezan
      if (!formData.region || formData.region.trim() === '') {
        return false;
      }
    } else {
      // Za Ponudu/Aukciju - adresa je obavezna
      if (!formData.address || formData.address.trim() === '') {
        return false;
      }
    }
    
    // Validacija kvadrature - obavezna samo za kategorije koje je prikazuju (osim za Potražnju gde je opseg)
    if (!isPotraznja && formConfig.showSquareMeters) {
      if (!formData.squareMeters || formData.squareMeters.trim() === '') {
        return false;
      }
    }
    
    // Validacija površine placa - obavezna za Plac kategoriju (osim za Potražnju)
    if (!isPotraznja && formData.category === 'Plac' && formConfig.showPlotArea) {
      if (!formData.plotArea || formData.plotArea.trim() === '') {
        return false;
      }
    }
    
    return true;
  };

  // Handler za promenu kategorije - resetuje polja koja nisu relevantna za novu kategoriju
  const handleCategoryChange = (newCategory: PropertyCategory | '') => {
    const newConfig = getFormConfig(newCategory);
    
    setFormData(prev => ({
      ...prev,
      category: newCategory,
      subcategory: '', // Uvek resetuj podkategoriju
      // Resetuj polja koja nisu relevantna za novu kategoriju
      squareMeters: newConfig.showSquareMeters ? prev.squareMeters : '',
      squareMetersMin: newConfig.showSquareMeters ? prev.squareMetersMin : '',
      squareMetersMax: newConfig.showSquareMeters ? prev.squareMetersMax : '',
      rooms: newConfig.showRooms ? prev.rooms : '',
      roomsMin: newConfig.showRooms ? prev.roomsMin : '',
      roomsMax: newConfig.showRooms ? prev.roomsMax : '',
      floor: newConfig.showFloor ? prev.floor : '',
      totalFloors: newConfig.showFloor ? prev.totalFloors : '',
      plotArea: newConfig.showPlotArea ? prev.plotArea : '',
      plotAreaMin: newConfig.showPlotArea ? prev.plotAreaMin : '',
      plotAreaMax: newConfig.showPlotArea ? prev.plotAreaMax : '',
      heating: newConfig.showHeating ? prev.heating : '',
      heatingTypes: newConfig.showHeating ? prev.heatingTypes : [],
      utilities: newConfig.showUtilities ? prev.utilities : '',
      // Filtriraj karakteristike da zadrži samo one relevantne za novu kategoriju
      features: Object.fromEntries(
        Object.entries(prev.features).filter(([key]) => 
          newConfig.features.includes(key as keyof PropertyFeatures)
        )
      ) as Partial<PropertyFeatures>
    }));
  };

  // Handler za promenu vrste oglasa (Ponuda/Potražnja/Aukcija)
  // POTPUNO resetuje formu osim kontakt podataka
  const handleAdKindChange = (newAdKind: AdKind) => {
    if (newAdKind === formData.adKind) return; // Nema promene
    
    // Odredi odgovarajući adType za novu vrstu
    // Potražnja: Kupovina/Rentiranje, Ponuda/Aukcija: Prodaja/Izdavanje
    const newAdType = newAdKind === 'Potražnja' ? 'Kupovina' : 'Prodaja';
    
    // Potpuno resetuj formu osim kontakt podataka
    setFormData({
      ...getDefaultFormData(),
      adKind: newAdKind,
      adType: newAdType as AdType,
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
    });
    
    // Resetuj slike i vrati na korak 1
    setImages([]);
    setStep(1);
  };

  // Sačuvaj kao draft (za slučaj kad nema dovoljno tokena)
  const handleSaveAsDraft = async () => {
    if (!formData.title || !formData.category) {
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Unesite bar naslov i kategoriju za draft' : 'Enter at least title and category for draft'
      );
      return;
    }

    setSavingDraft(true);
    try {
      // Sačuvaj draft u localStorage (trajno, za razliku od sessionStorage)
      const draftData = {
        ...formData,
        images,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      
      toast.success(
        language === 'sr' ? 'Sačuvano' : 'Saved',
        language === 'sr' ? 'Oglas je sačuvan kao draft. Možete ga nastaviti kasnije.' : 'Listing saved as draft. You can continue later.'
      );
      
      // Očisti sessionStorage i vrati na moje oglase
      sessionStorage.removeItem(FORM_STORAGE_KEY);
      navigate('/moji-oglasi');
    } catch (err) {
      toast.error(
        language === 'sr' ? 'Greška' : 'Error',
        language === 'sr' ? 'Greška pri čuvanju drafta' : 'Error saving draft'
      );
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Back Button */}
        <BackButton />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-slate-900 dark:bg-slate-950 text-white p-6">
            <h1 className="text-2xl font-bold mb-2">{language === 'sr' ? 'Objavi novi oglas' : 'Post New Listing'}</h1>
            <p className="text-slate-400">{language === 'sr' ? 'Korak' : 'Step'} {step} {language === 'sr' ? 'od' : 'of'} 3</p>
            <div className="flex gap-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`flex-1 h-2 rounded-full ${s <= step ? 'bg-amber-500' : 'bg-slate-700'}`}
                />
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                  {language === 'sr' ? 'Osnovne informacije' : 'Basic Information'}
                </h2>
                
                {/* Ad Kind */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Vrsta oglasa' : 'Listing Type'}
                  </label>
                  <div className="flex gap-2">
                    {([
                      { value: 'Ponuda', label: language === 'sr' ? 'Ponuda' : 'Offer' },
                      { value: 'Potražnja', label: language === 'sr' ? 'Potražnja' : 'Wanted' },
                      { value: 'Aukcija', label: language === 'sr' ? 'Aukcija' : 'Auction' }
                    ] as { value: AdKind; label: string }[]).map((kind) => (
                      <button
                        key={kind.value}
                        onClick={() => handleAdKindChange(kind.value)}
                        className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                          formData.adKind === kind.value
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {kind.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ad Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Tip' : 'Type'}
                  </label>
                  <div className="flex gap-2">
                    {(formData.adKind === 'Potražnja' ? [
                      { value: 'Kupovina', label: language === 'sr' ? 'Kupovina' : 'Buying' },
                      { value: 'Rentiranje', label: language === 'sr' ? 'Rentiranje' : 'Renting' }
                    ] : [
                      { value: 'Prodaja', label: language === 'sr' ? 'Prodaja' : 'For Sale' },
                      { value: 'Izdavanje', label: language === 'sr' ? 'Izdavanje' : 'For Rent' }
                    ] as { value: AdType; label: string }[]).map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setFormData({ ...formData, adType: type.value })}
                        className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                          formData.adType === type.value
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Kategorija' : 'Category'}
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleCategoryChange(e.target.value as PropertyCategory)}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">{language === 'sr' ? 'Izaberi kategoriju' : 'Select category'}</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat.name} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory */}
                {selectedCategory && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Podkategorija' : 'Subcategory'}
                    </label>
                    <select
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value as PropertySubcategory })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">{language === 'sr' ? 'Izaberi podkategoriju' : 'Select subcategory'}</option>
                      {selectedCategory.subcategories.map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Naslov oglasa' : 'Listing Title'}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={language === 'sr' ? 'npr. Luksuzan stan na Vračaru' : 'e.g. Luxurious apartment in downtown'}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Opis' : 'Description'}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder={language === 'sr' ? 'Opišite nekretninu...' : 'Describe the property...'}
                    className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <button
                  onClick={() => setStep(2)}
                  disabled={!formData.category || !formData.title}
                  className="w-full py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {language === 'sr' ? 'Nastavi' : 'Continue'}
                </button>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                  {language === 'sr' ? 'Detalji nekretnine' : 'Property Details'}
                </h2>

                {/* Price - različito za Potražnju (opseg) i Ponudu/Aukciju (tačna vrednost) */}
                {formData.adKind === 'Potražnja' ? (
                  // Za Potražnju - opseg cene (Od - Do)
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Cena (opseg)' : 'Price (range)'}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <input
                          type="number"
                          min="0"
                          value={formData.priceMin}
                          onChange={(e) => setFormData({ ...formData, priceMin: e.target.value })}
                          placeholder={language === 'sr' ? 'Od' : 'From'}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          value={formData.priceMax}
                          onChange={(e) => setFormData({ ...formData, priceMax: e.target.value })}
                          placeholder={language === 'sr' ? 'Do' : 'To'}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <select
                          value={formData.currency}
                          onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'EUR' | 'RSD' })}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="EUR">EUR</option>
                          <option value="RSD">RSD</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Za Ponudu i Aukciju - tačna cena
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'sr' ? 'Cena *' : 'Price *'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="0"
                        className={`w-full px-4 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 ${
                          !formData.price?.trim() ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'sr' ? 'Valuta' : 'Currency'}
                      </label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'EUR' | 'RSD' })}
                        className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="EUR">EUR</option>
                        <option value="RSD">RSD</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Location - različito za Potražnju (region/grad) i Ponudu/Aukciju (tačna adresa) */}
                {formData.adKind === 'Potražnja' ? (
                  // Za Potražnju - samo region i grad, opciono lokacija u blizini
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Region' : 'Region'}
                        </label>
                        <select
                          value={formData.region}
                          onChange={(e) => {
                            const newRegion = e.target.value;
                            setFormData({ ...formData, region: newRegion, city: '' });
                            // Center map on region if selected
                            if (newRegion) {
                              const region = REGIONS.find(r => r.name === newRegion);
                              if (region?.lat && region?.lng) {
                                setLocationCoords({ lat: region.lat, lng: region.lng });
                              }
                            }
                          }}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">{language === 'sr' ? 'Izaberi region' : 'Select region'}</option>
                          {REGIONS.map((r) => (
                            <option key={r.id} value={r.name}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Grad/Opština' : 'City'}
                        </label>
                        <SubregionSearch
                          value={formData.city}
                          regionValue={formData.region}
                          onChange={(city, region, lat, lng) => {
                            setFormData(prev => ({
                              ...prev,
                              city: city,
                              region: region || prev.region
                            }));
                            if (lat && lng) {
                              setLocationCoords({ lat, lng });
                            }
                          }}
                          placeholder={language === 'sr' ? 'Pretraži grad/opštinu...' : 'Search city...'}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'sr' ? 'Tačna adresa (opciono)' : 'Exact address (optional)'}
                      </label>
                      <LocationSearch
                        value={formData.nearbyLocation || ''}
                        onChange={(value, lat, lng, addressDetails) => {
                          setFormData(prev => ({ ...prev, nearbyLocation: value }));
                          if (lat && lng) {
                            setLocationCoords({ lat, lng });
                          }
                          // Auto-fill region/city from address
                          if (addressDetails) {
                            const normalize = (str: string) => str
                              .toLowerCase()
                              .normalize('NFD')
                              .replace(/[\u0300-\u036f]/g, '')
                              .replace(/đ/g, 'd').replace(/ć/g, 'c').replace(/č/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z');
                            
                            let city = addressDetails.city || addressDetails.municipality || '';
                            for (const r of REGIONS) {
                              const foundCity = r.cities.find(c =>
                                normalize(c.name) === normalize(city) ||
                                normalize(city).includes(normalize(c.name))
                              );
                              if (foundCity) {
                                setFormData(prev => ({
                                  ...prev,
                                  nearbyLocation: value,
                                  city: foundCity.name,
                                  region: r.name
                                }));
                                return;
                              }
                            }
                          }
                        }}
                        placeholder={language === 'sr' ? 'Pretraži ulicu, adresu...' : 'Search street, address...'}
                        showOsmSearch={true}
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {language === 'sr'
                          ? 'Opciono: Unesite tačnu lokaciju ako tražite nekretninu u blizini određenog mesta'
                          : 'Optional: Enter specific location if looking for property near a specific place'}
                      </p>
                    </div>
                    
                    {/* Interactive Map for Potražnja */}
                    <div>
                      <LocationPickerMap
                        initialLat={locationCoords.lat}
                        initialLng={locationCoords.lng}
                        onLocationSelect={(lat, lng, address) => {
                          setLocationCoords({ lat, lng });
                          if (address) {
                            setFormData(prev => ({ ...prev, nearbyLocation: address }));
                            // Parse address for region/city
                            const normalize = (str: string) => str
                              .toLowerCase()
                              .normalize('NFD')
                              .replace(/[\u0300-\u036f]/g, '')
                              .replace(/đ/g, 'd').replace(/ć/g, 'c').replace(/č/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z');
                            
                            const parts = address.split(',').map(p => p.trim());
                            for (const part of parts) {
                              for (const r of REGIONS) {
                                const foundCity = r.cities.find(c =>
                                  normalize(c.name) === normalize(part) ||
                                  normalize(part).includes(normalize(c.name))
                                );
                                if (foundCity) {
                                  setFormData(prev => ({
                                    ...prev,
                                    nearbyLocation: address,
                                    city: foundCity.name,
                                    region: r.name
                                  }));
                                  return;
                                }
                              }
                            }
                          }
                        }}
                        height="200px"
                      />
                    </div>
                  </div>
                ) : (
                  // Za Ponudu i Aukciju - tačna adresa sa OSM pretragom
                  <>
                    {/* Region and City - with search and auto-sync */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Region' : 'Region'}
                        </label>
                        <select
                          value={formData.region}
                          onChange={(e) => {
                            const newRegion = e.target.value;
                            setFormData({ ...formData, region: newRegion, city: '' });
                            // Center map on region
                            if (newRegion) {
                              const region = REGIONS.find(r => r.name === newRegion);
                              if (region?.lat && region?.lng) {
                                setLocationCoords({ lat: region.lat, lng: region.lng });
                              }
                            }
                          }}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">{language === 'sr' ? 'Izaberi region' : 'Select region'}</option>
                          {REGIONS.map((r) => (
                            <option key={r.id} value={r.name}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Grad/Opština' : 'City'}
                        </label>
                        <SubregionSearch
                          value={formData.city}
                          regionValue={formData.region}
                          onChange={(city, region, lat, lng) => {
                            setFormData(prev => ({
                              ...prev,
                              city: city,
                              region: region || prev.region
                            }));
                            if (lat && lng) {
                              setLocationCoords({ lat, lng });
                            }
                          }}
                          placeholder={language === 'sr' ? 'Pretraži grad/opštinu...' : 'Search city...'}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'sr' ? 'Tačna adresa (ulica, broj...) *' : 'Exact address (street, number...) *'}
                      </label>
                      <LocationSearch
                        value={formData.address || ''}
                        onChange={(value, lat, lng, addressDetails) => {
                          const normalize = (str: string) => str
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .replace(/đ/g, 'd').replace(/ć/g, 'c').replace(/č/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z');
                          
                          if (addressDetails) {
                            let city = addressDetails.city || addressDetails.municipality || '';
                            let matchedCity = '';
                            let matchedRegion = '';

                            for (const r of REGIONS) {
                              const foundCity = r.cities.find(c =>
                                normalize(c.name) === normalize(city) ||
                                normalize(city).includes(normalize(c.name)) ||
                                normalize(c.name).includes(normalize(city))
                              );
                              if (foundCity) {
                                matchedCity = foundCity.name;
                                matchedRegion = r.name;
                                break;
                              }
                            }

                            setFormData(prev => ({
                              ...prev,
                              address: value,
                              city: matchedCity || prev.city,
                              region: matchedRegion || prev.region
                            }));
                            if (lat && lng) {
                              setLocationCoords({ lat, lng });
                            }
                            return;
                          }

                          // Fallback
                          const parts = value.split(',').map(p => p.trim());
                          for (const r of REGIONS) {
                            for (const part of parts) {
                              const foundCity = r.cities.find(c =>
                                normalize(c.name) === normalize(part)
                              );
                              if (foundCity) {
                                setFormData(prev => ({
                                  ...prev,
                                  address: value,
                                  city: foundCity.name,
                                  region: r.name
                                }));
                                if (lat && lng) setLocationCoords({ lat, lng });
                                return;
                              }
                            }
                          }
                          
                          setFormData(prev => ({ ...prev, address: value }));
                          if (lat && lng) setLocationCoords({ lat, lng });
                        }}
                        placeholder={language === 'sr' ? 'Pretraži ulicu, adresu...' : 'Search street, address...'}
                        showOsmSearch={true}
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {language === 'sr'
                          ? 'Region i grad/opština će se automatski popuniti iz pretrage'
                          : 'Region and city will be auto-filled from search'}
                      </p>
                    </div>

                    {/* Interactive Map for location selection */}
                    <div>
                      <LocationPickerMap
                        initialLat={locationCoords.lat}
                        initialLng={locationCoords.lng}
                        onLocationSelect={(lat, lng, address) => {
                          setLocationCoords({ lat, lng });
                          if (address) {
                            const normalize = (str: string) => str
                              .toLowerCase()
                              .normalize('NFD')
                              .replace(/[\u0300-\u036f]/g, '')
                              .replace(/đ/g, 'd').replace(/ć/g, 'c').replace(/č/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z');
                            
                            const parts = address.split(',').map(p => p.trim());
                            let matchedCity = '';
                            let matchedRegion = '';
                            
                            for (const part of parts) {
                              for (const r of REGIONS) {
                                const foundCity = r.cities.find(c =>
                                  normalize(c.name) === normalize(part) ||
                                  normalize(part).includes(normalize(c.name))
                                );
                                if (foundCity) {
                                  matchedCity = foundCity.name;
                                  matchedRegion = r.name;
                                  break;
                                }
                              }
                              if (matchedCity) break;
                            }
                            
                            setFormData(prev => ({
                              ...prev,
                              address: address,
                              city: matchedCity || prev.city,
                              region: matchedRegion || prev.region
                            }));
                          }
                        }}
                        height="250px"
                      />
                    </div>
                  </>
                )}

                {/* Size, Rooms, Plot Area - dinamički na osnovu kategorije i vrste oglasa */}
                {formData.adKind === 'Potražnja' ? (
                  // Za Potražnju - opsezi (Od - Do)
                  <div className="space-y-4">
                    {/* Kvadratura - opseg */}
                    {formConfig.showSquareMeters && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Kvadratura (m²) - opseg' : 'Area (m²) - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            value={formData.squareMetersMin}
                            onChange={(e) => setFormData({ ...formData, squareMetersMin: e.target.value })}
                            placeholder={language === 'sr' ? 'Od' : 'From'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                          <input
                            type="number"
                            min="0"
                            value={formData.squareMetersMax}
                            onChange={(e) => setFormData({ ...formData, squareMetersMax: e.target.value })}
                            placeholder={language === 'sr' ? 'Do' : 'To'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Broj soba - multiselekcija/opseg */}
                    {formConfig.showRooms && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Broj soba - opseg' : 'Rooms - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={formData.roomsMin}
                            onChange={(e) => setFormData({ ...formData, roomsMin: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Od (min)' : 'From (min)'}</option>
                            {['0.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5+'].map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                          <select
                            value={formData.roomsMax}
                            onChange={(e) => setFormData({ ...formData, roomsMax: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Do (max)' : 'To (max)'}</option>
                            {['0.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5+'].map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Sprat - opseg za Potražnju */}
                    {formConfig.showFloor && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Željeni sprat - opseg' : 'Desired floor - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={formData.floorMin}
                            onChange={(e) => setFormData({ ...formData, floorMin: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Od (min)' : 'From (min)'}</option>
                            <option value="-3">{language === 'sr' ? '-2 ili niže' : '-2 or lower'}</option>
                            <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="S">{language === 'sr' ? 'Suteren' : 'Semi-basement'}</option>
                            <option value="P">{language === 'sr' ? 'Prizemlje' : 'Ground floor'}</option>
                            <option value="M">{language === 'sr' ? 'Međusprat' : 'Mezzanine'}</option>
                            {[1, 2, 3, 4, 5, 6, 7].map(f => (
                              <option key={f} value={String(f)}>{f}</option>
                            ))}
                            <option value="8">8+</option>
                            <option value="Pk">{language === 'sr' ? 'Potkrovlje' : 'Attic'}</option>
                            <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                            <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                          </select>
                          <select
                            value={formData.floorMax}
                            onChange={(e) => setFormData({ ...formData, floorMax: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Do (max)' : 'To (max)'}</option>
                            <option value="-3">{language === 'sr' ? '-2 ili niže' : '-2 or lower'}</option>
                            <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="S">{language === 'sr' ? 'Suteren' : 'Semi-basement'}</option>
                            <option value="P">{language === 'sr' ? 'Prizemlje' : 'Ground floor'}</option>
                            <option value="M">{language === 'sr' ? 'Međusprat' : 'Mezzanine'}</option>
                            {[1, 2, 3, 4, 5, 6, 7].map(f => (
                              <option key={f} value={String(f)}>{f}</option>
                            ))}
                            <option value="8">8+</option>
                            <option value="Pk">{language === 'sr' ? 'Potkrovlje' : 'Attic'}</option>
                            <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                            <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Površina placa - opseg */}
                    {formConfig.showPlotArea && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Površina placa (ari) - opseg' : 'Plot area (ar) - range'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="0"
                            value={formData.plotAreaMin}
                            onChange={(e) => setFormData({ ...formData, plotAreaMin: e.target.value })}
                            placeholder={language === 'sr' ? 'Od' : 'From'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                          <input
                            type="number"
                            min="0"
                            value={formData.plotAreaMax}
                            onChange={(e) => setFormData({ ...formData, plotAreaMax: e.target.value })}
                            placeholder={language === 'sr' ? 'Do' : 'To'}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Grejanje - multiselekcija za Potražnju */}
                    {formConfig.showHeating && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Grejanje (može više opcija)' : 'Heating (multiple options)'}
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {[
                            { value: 'CG', label: language === 'sr' ? 'Centralno' : 'Central' },
                            { value: 'EG', label: language === 'sr' ? 'Etažno' : 'Floor' },
                            { value: 'TA', label: 'TA peć' },
                            { value: 'Gas', label: 'Gas' },
                            { value: 'Drvo', label: language === 'sr' ? 'Drvo' : 'Wood' },
                          ].map(h => (
                            <label key={h.value} className="flex items-center gap-2 p-2 rounded-lg bg-slate-100 dark:bg-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600">
                              <input
                                type="checkbox"
                                checked={formData.heatingTypes?.includes(h.value as HeatingType) || false}
                                onChange={(e) => {
                                  const types = formData.heatingTypes || [];
                                  if (e.target.checked) {
                                    setFormData({ ...formData, heatingTypes: [...types, h.value as HeatingType] });
                                  } else {
                                    setFormData({ ...formData, heatingTypes: types.filter(t => t !== h.value) });
                                  }
                                }}
                                className="rounded text-amber-500 focus:ring-amber-500"
                              />
                              <span className="text-sm text-slate-700 dark:text-slate-300">{h.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Za Ponudu i Aukciju - tačne vrednosti
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Kvadratura - za Kuća, Stan, Poslovni prostor */}
                    {formConfig.showSquareMeters && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Kvadratura (m²) *' : 'Area (m²) *'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.squareMeters}
                          onChange={(e) => setFormData({ ...formData, squareMeters: e.target.value })}
                          className={`w-full px-4 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 ${
                            !formData.squareMeters?.trim() ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                          }`}
                        />
                      </div>
                    )}
                    
                    {/* Sobe - za Kuća, Stan, Poslovni prostor */}
                    {formConfig.showRooms && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Broj soba' : 'Rooms'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={formData.rooms}
                          onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    )}
                    
                    {/* Površina placa - za Kuća i Plac */}
                    {formConfig.showPlotArea && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? (formData.category === 'Plac' ? 'Površina placa (ari) *' : 'Površina placa (ari)') : (formData.category === 'Plac' ? 'Plot area (ar) *' : 'Plot area (ar)')}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.plotArea}
                          onChange={(e) => setFormData({ ...formData, plotArea: e.target.value })}
                          className={`w-full px-4 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 ${
                            formData.category === 'Plac' && !formData.plotArea?.trim() ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
                          }`}
                        />
                      </div>
                    )}
                    
                    {/* Sprat - za Stan i Poslovni prostor */}
                    {formConfig.showFloor && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Sprat' : 'Floor'}
                          </label>
                          <select
                            value={formData.floor}
                            onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Izaberite sprat' : 'Select floor'}</option>
                            <option value="-3">{language === 'sr' ? '-2 ili niže (Duboki podrum)' : '-2 or lower (Deep basement)'}</option>
                            <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                            <option value="S">{language === 'sr' ? 'Suteren (S)' : 'Semi-basement (S)'}</option>
                            <option value="P">{language === 'sr' ? 'Prizemlje (P)' : 'Ground floor (P)'}</option>
                            <option value="M">{language === 'sr' ? 'Međusprat (M)' : 'Mezzanine (M)'}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8+</option>
                            <option value="Pk">{language === 'sr' ? 'Potkrovlje (Pk)' : 'Attic (Pk)'}</option>
                            <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                            <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {language === 'sr' ? 'Ukupno spratova' : 'Total floors'}
                          </label>
                          <select
                            value={formData.totalFloors}
                            onChange={(e) => setFormData({ ...formData, totalFloors: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">{language === 'sr' ? 'Izaberite' : 'Select'}</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                            <option value="10">10</option>
                            <option value="11">11+</option>
                          </select>
                        </div>
                      </>
                    )}
                    
                    {/* Grejanje - za Kuća i Stan */}
                    {formConfig.showHeating && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Grejanje' : 'Heating'}
                        </label>
                        <select
                          value={formData.heating}
                          onChange={(e) => setFormData({ ...formData, heating: e.target.value as HeatingType })}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">{language === 'sr' ? 'Izaberite' : 'Select'}</option>
                          <option value="CG">{language === 'sr' ? 'Centralno grejanje' : 'Central heating'}</option>
                          <option value="EG">{language === 'sr' ? 'Etažno grejanje' : 'Floor heating'}</option>
                          <option value="TA">{language === 'sr' ? 'TA peć' : 'Storage heater'}</option>
                          <option value="Gas">{language === 'sr' ? 'Gas' : 'Gas'}</option>
                          <option value="Drvo">{language === 'sr' ? 'Drvo' : 'Wood'}</option>
                          <option value="Bez grejanja">{language === 'sr' ? 'Bez grejanja' : 'No heating'}</option>
                        </select>
                      </div>
                    )}
                    
                    {/* Režije - za Kuća, Stan, Poslovni prostor */}
                    {formConfig.showUtilities && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {language === 'sr' ? 'Režije (EUR/mesec)' : 'Utilities (EUR/month)'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.utilities}
                          onChange={(e) => setFormData({ ...formData, utilities: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Media Upload (Images + Videos) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? `Slike i video (${images.length}/${MAX_MEDIA_COUNT})` : `Images & Video (${images.length}/${MAX_MEDIA_COUNT})`}
                  </label>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4">
                    {images.length < MAX_MEDIA_COUNT && (
                      <label className="flex flex-col items-center justify-center cursor-pointer py-4">
                        <svg className="w-8 h-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {language === 'sr' ? 'Kliknite za dodavanje slika ili video snimaka' : 'Click to add images or videos'}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          {language === 'sr' ? `Slike do ${MAX_IMAGE_SIZE / 1024 / 1024}MB, video do ${MAX_VIDEO_SIZE / 1024 / 1024}MB` : `Images up to ${MAX_IMAGE_SIZE / 1024 / 1024}MB, video up to ${MAX_VIDEO_SIZE / 1024 / 1024}MB`}
                        </span>
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                    {images.length > 0 && (
                      <div className="grid grid-cols-5 gap-2 mt-4">
                        {images.map((img, idx) => (
                          <div key={idx} className="relative">
                            {img.startsWith('data:video/') ? (
                              <div className="w-full h-16 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center">
                                <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            ) : (
                              <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-16 object-cover rounded" />
                            )}
                            <button
                              onClick={() => removeImage(idx)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Features - filtrirane prema kategoriji */}
                {filteredFeatureOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Karakteristike' : 'Features'}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {filteredFeatureOptions.map((f) => (
                        <label key={f.key} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.features[f.key]}
                            onChange={(e) => setFormData({
                              ...formData,
                              features: { ...formData.features, [f.key]: e.target.checked }
                            })}
                            className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* HITNO / Urgent Toggle */}
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.isUrgent || false}
                      onChange={(e) => setFormData({ ...formData, isUrgent: e.target.checked })}
                      className="w-5 h-5 rounded border-red-300 dark:border-red-600 text-red-500 focus:ring-red-500"
                    />
                    <div>
                      <span className="flex items-center gap-2 text-base font-bold text-red-600 dark:text-red-400">
                        🔥 {language === 'sr' ? 'HITNO!' : 'URGENT!'}
                      </span>
                      <p className="text-sm text-red-500 dark:text-red-400 mt-1">
                        {language === 'sr' 
                          ? 'Označite ako je oglas hitan - olakšava pretragu hitnih oglasa' 
                          : 'Mark if listing is urgent - makes it easier to find urgent listings'}
                      </p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {language === 'sr' ? 'Nazad' : 'Back'}
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!validateStep2()}
                    className="flex-1 py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {language === 'sr' ? 'Nastavi' : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Contact & Duration */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                  {language === 'sr' ? 'Kontakt i trajanje' : 'Contact & Duration'}
                </h2>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'sr' ? 'Telefon *' : 'Phone *'}
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      className={`w-full px-4 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 ${
                        !formData.contactPhone.trim() 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                      placeholder={language === 'sr' ? 'Unesite broj telefona' : 'Enter phone number'}
                    />
                    {!formData.contactPhone.trim() && (
                      <p className="text-xs text-red-500 mt-1">
                        {language === 'sr' ? 'Obavezno polje' : 'Required field'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Trajanje oglasa' : 'Listing Duration'}
                  </label>
                  {loadingDurations ? (
                    <div className="text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Učitavanje opcija...' : 'Loading options...'}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {durationOptions.filter(d => d.is_active).map((opt) => (
                        <button
                          key={opt.days}
                          onClick={() => setFormData({ ...formData, duration: opt.days })}
                          className={`p-3 rounded-lg border transition-colors ${
                            formData.duration === opt.days
                              ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                              : 'border-slate-300 dark:border-slate-600 hover:border-amber-300'
                          }`}
                        >
                          <div className="font-semibold text-slate-900 dark:text-white">{opt.label}</div>
                          <div className="text-sm text-amber-600">{opt.tokens} token{opt.tokens > 1 ? (language === 'sr' ? 'a' : 's') : ''}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    {language === 'sr' ? 'Rezime' : 'Summary'}
                  </h3>
                  <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                    <p>{language === 'sr' ? 'Vrsta' : 'Type'}: {formData.adKind} - {formData.adType}</p>
                    <p>{language === 'sr' ? 'Kategorija' : 'Category'}: {formData.category} {formData.subcategory && `/ ${formData.subcategory}`}</p>
                    <p>{language === 'sr' ? 'Lokacija' : 'Location'}: {formData.region}, {formData.city}</p>
                    <p>{language === 'sr' ? 'Cena' : 'Price'}: {formData.price} {formData.currency}</p>
                    <p>{language === 'sr' ? 'Trajanje' : 'Duration'}: {selectedDuration?.label}</p>
                    {images.length > 0 && <p>{language === 'sr' ? 'Slike' : 'Images'}: {images.length}</p>}
                    <hr className="my-2 border-slate-300 dark:border-slate-600" />
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {language === 'sr' ? 'Cena objave' : 'Posting cost'}: {tokensRequired} token{tokensRequired > 1 ? (language === 'sr' ? 'a' : 's') : ''}
                    </p>
                    <p className="text-xs">
                      {language === 'sr' ? 'Vaš balans' : 'Your balance'}: {loadingTokens ? '...' : tokenBalance} {language === 'sr' ? 'tokena' : 'tokens'}
                    </p>
                  </div>
                </div>

                {tokenBalance < tokensRequired && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      {language === 'sr' 
                        ? `Nemate dovoljno tokena. Potrebno je ${tokensRequired}, a imate ${tokenBalance}.`
                        : `Not enough tokens. You need ${tokensRequired}, but have ${tokenBalance}.`}
                    </p>
                    <button
                      onClick={handleBuyTokens}
                      className="mt-2 text-sm text-amber-600 hover:text-amber-500 font-medium"
                    >
                      {language === 'sr' ? 'Kupi tokene →' : 'Buy tokens →'}
                    </button>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {language === 'sr' ? 'Vaši podaci će biti sačuvani.' : 'Your data will be saved.'}
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {language === 'sr' ? 'Nazad' : 'Back'}
                  </button>
                  
                  {/* Save as Draft button - always available */}
                  <button
                    onClick={() => handleSubmit(true)}
                    disabled={isSubmitting || savingDraft}
                    className="flex-1 py-3 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg font-semibold hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                  >
                    {savingDraft 
                      ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') 
                      : (language === 'sr' ? 'Sačuvaj u pripremi' : 'Save as Draft')}
                  </button>
                  
                  {tokenBalance >= tokensRequired && (
                    <button
                      onClick={() => handleSubmit(false)}
                      disabled={isSubmitting}
                      className="flex-1 py-3 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting 
                        ? (language === 'sr' ? 'Objavljujem...' : 'Posting...') 
                        : `${language === 'sr' ? 'Objavi oglas' : 'Post listing'} (${tokensRequired} ${language === 'sr' ? 'tokena' : 'tokens'})`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
